import React, { useEffect, useRef, useState } from 'react';
import { Button, Container } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { baseUrl } from '../../constants/baseUrl';
import { routesPath } from '../../constants/routesPath';
import { completeSignIn, forgetSession, signOutAfterRefusal } from '../../services/keycloakService';
import storageService from '../../services/storageService';

// The other tab may still be opening the account when this one arrives.
const signedInByAnotherTab = async () => {
  for (let attempt = 0; attempt < 16; attempt += 1) {
    if (storageService.getToken()) return true;
    await new Promise((resolve) => setTimeout(resolve, 250));
  }
  return false;
};

/** Return point after Keycloak sign-in (HOLM-506). */
export const KeycloakCallbackContainer = () => {
  const navigate = useNavigate();
  const started = useRef(false);
  const [errorMessage, setErrorMessage] = useState('');
  // Kept in memory only, to end the Holedo sign-in if the person asks to.
  const [refusedIdToken, setRefusedIdToken] = useState('');

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    (async () => {
      let returned = false;
      let idToken = '';
      try {
        const { token, returnPath, idToken: signedInIdToken } = await completeSignIn();
        returned = true;
        idToken = signedInIdToken || '';
        // Remove the code and state from the address bar and history.
        window.history.replaceState({}, document.title, routesPath.keycloakCallback);

        const res = await fetch(`${baseUrl}/rest/users/me/?full=true`, {
          headers: { Accept: 'application/json', AuthAPI: `Bearer ${token}` },
        });
        const body = await res.json().catch(() => null);
        const user = body?.data?.user;
        if (!res.ok || !body?.success || !user?.id) {
          const errors = body?.errors;
          throw new Error(
            (Array.isArray(errors) ? errors[0] : errors) ||
              'Holedo could not open your account. Please contact support.',
          );
        }

        storageService.setAuthenticatedSession({ token, user, keepKeycloakSession: true });
        toast.success('Successfully signed in!');
        navigate(returnPath === '/' ? routesPath.dashboard : returnPath, { replace: true });
      } catch (error) {
        window.history.replaceState({}, document.title, routesPath.keycloakCallback);
        // This sign-in was already finished in another tab of this browser
        // (the link in a Holedo email opens one, and the page that was
        // waiting then continues to here): carry on signed in.
        const usedElsewhere = !returned && /state/i.test(error?.message || '');
        if (usedElsewhere && (await signedInByAnotherTab())) {
          navigate(routesPath.dashboard, { replace: true });
          return;
        }
        // TMR did not open (for example the account has no TMR access):
        // do not keep a Keycloak session in this browser's storage. A return
        // that matched no sign-in of this browser stored nothing to remove.
        if (!usedElsewhere) await forgetSession();
        if (returned) setRefusedIdToken(idToken);
        setErrorMessage(
          usedElsewhere
            ? 'This sign-in link has expired. Please sign in again.'
            : error?.message || 'Sign-in could not be completed.',
        );
      }
    })();
  }, [navigate]);

  return (
    <Container className="py-5 text-center">
      {!errorMessage ? (
        <h2>Signing you in...</h2>
      ) : (
        <>
          <h2>We could not sign you in</h2>
          <p>{errorMessage}</p>
          <Button color="primary" onClick={() => navigate(routesPath.login, { replace: true })}>
            Back to login
          </Button>
          {refusedIdToken && (
            <>
              {' '}
              <Button
                color="secondary"
                onClick={() =>
                  signOutAfterRefusal(refusedIdToken).catch(() => navigate(routesPath.login, { replace: true }))
                }
              >
                Use a different account
              </Button>
            </>
          )}
        </>
      )}
    </Container>
  );
};
